/**
 * GOVOPS-1113 — Unified single-permit intake pipeline.
 * All create paths use validatePermitIntake → submitPermitIntake → GovOps (status: intake).
 */
import { createPermitRecord } from '../api/govopsPermitService';
import { buildCreatePermitPayload, normalizeAlarmLocation } from './permitFields';
import { defaultFeeFields } from './feeFields';
import { validateCoordinates } from './mapCoordinates';

/** Where the intake UI lives — stored in permit program for audit context. */
export const INTAKE_SOURCES = {
  DASHBOARD: 'dashboard',
  ADMIN: 'admin',
  MAP_SUBMIT: 'map_submit',
  FULL_FORM: 'full_form',
  LEGACY_CREATE: 'legacy_create',
};

/** Validation profile for each intake surface. */
export const INTAKE_MODES = {
  QUICK: 'quick',
  MAP: 'map',
  FULL: 'full',
};

export function emptyQuickIntakeForm() {
  return {
    type: '',
    description: '',
    latitude: '',
    longitude: '',
    date: '',
    permitNumber: '',
    street: '',
    unit: '',
    city: '',
    state: '',
    zipCode: '',
    coordinates: null,
    alarmLocation: { street: '', unit: '', city: '', state: '', zipCode: '' },
    ...defaultFeeFields(),
  };
}

export function emptyFullIntakeForm() {
  return {
    ...emptyQuickIntakeForm(),
    applicantName: '',
    phoneNumber: '',
    contactName: '',
    alternateNumber: '',
    email: '',
    billingName: '',
    billingStreet: '',
    billingUnit: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    alarmCompany: '',
    alarmPhone: '',
    propertyType: 'Residential',
    checkNumber: '',
  };
}

function resolveCoordinates(form) {
  if (form.coordinates?.latitude != null && form.coordinates?.longitude != null) {
    const check = validateCoordinates(
      form.coordinates.latitude,
      form.coordinates.longitude
    );
    if (check.ok) {
      return { latitude: check.lat, longitude: check.lng };
    }
  }
  if (form.latitude !== '' && form.longitude !== '') {
    const check = validateCoordinates(form.latitude, form.longitude);
    if (check.ok) {
      return { latitude: check.lat, longitude: check.lng };
    }
    return { error: check.message };
  }
  return null;
}

/**
 * @returns {{ ok: boolean, errors: string[], coordinates?: { latitude: number, longitude: number } }}
 */
export function validatePermitIntake(form, mode = INTAKE_MODES.QUICK) {
  const errors = [];
  const coordResult = resolveCoordinates(form);

  if (!coordResult || coordResult.error) {
    errors.push(
      coordResult?.error ||
        'Valid coordinates are required. Enter latitude and longitude or select an address on the map.'
    );
  }

  const typeLabel = (form.type || form.propertyType || '').trim();
  const description = (form.description || form.applicantName || '').trim();

  if (mode === INTAKE_MODES.QUICK || mode === INTAKE_MODES.MAP) {
    if (!typeLabel) errors.push('Permit type is required.');
    if (!description) errors.push('Description is required.');
  }

  if (mode === INTAKE_MODES.FULL) {
    if (!form.applicantName?.trim()) errors.push('Applicant name is required.');
    if (!form.phoneNumber?.trim()) errors.push('Phone number is required.');
    if (!form.street?.trim()) errors.push('Street address is required.');
    if (!form.city?.trim()) errors.push('City is required.');
    if (!form.zipCode?.trim()) errors.push('Zip code is required.');
    if (!form.contactName?.trim()) errors.push('Contact name is required.');
    if (!form.email?.trim()) errors.push('Contact email is required.');
    if (!form.billingName?.trim()) errors.push('Billing name is required.');
    if (!form.billingStreet?.trim()) errors.push('Billing street is required.');
    if (!form.billingCity?.trim()) errors.push('Billing city is required.');
    if (!form.billingState?.trim()) errors.push('Billing state is required.');
    if (!form.billingZip?.trim()) errors.push('Billing zip is required.');
    if (!typeLabel && !form.propertyType?.trim()) {
      errors.push('Property type is required.');
    }
  }

  if (errors.length) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    errors: [],
    coordinates: coordResult,
  };
}

/** Map unified form state → buildCreatePermitPayload input. */
export function formToCreatePayload(form, mode) {
  const alarmLocation = normalizeAlarmLocation({
    ...form,
    alarmLocation: form.alarmLocation || {
      street: form.street,
      unit: form.unit,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
    },
  });

  const coordinates = resolveCoordinates(form);
  const base = {
    type: form.type || form.propertyType,
    description:
      form.description ||
      (mode === INTAKE_MODES.FULL ? form.applicantName : '') ||
      'Fire alarm permit',
    status: 'intake',
    coordinates,
    alarmLocation,
    street: alarmLocation.street,
    unit: alarmLocation.unit,
    city: alarmLocation.city,
    state: alarmLocation.state,
    zipCode: alarmLocation.zipCode,
    applicantName: form.applicantName,
    phoneNumber: form.phoneNumber,
    date: form.date,
    permitNumber: form.permitNumber,
    propertyType: form.propertyType,
    checkNumber: form.checkNumber,
  };

  if (mode === INTAKE_MODES.FULL) {
    Object.assign(base, {
      contact: {
        name: form.contactName,
        alternateNumber: form.alternateNumber,
        email: form.email,
      },
      billingAddress: {
        name: form.billingName,
        street: form.billingStreet,
        unit: form.billingUnit,
        city: form.billingCity,
        state: form.billingState,
        zipCode: form.billingZip,
      },
      alarmCompany: {
        name: form.alarmCompany,
        phone: form.alarmPhone,
      },
    });
  }

  return base;
}

/**
 * Validate and create a permit at workflow status intake.
 * GovOps records permit_created audit server-side.
 */
export async function submitPermitIntake(form, { source, mode = INTAKE_MODES.QUICK }) {
  const validation = validatePermitIntake(form, mode);
  if (!validation.ok) {
    const err = new Error(validation.errors.join(' '));
    err.validationErrors = validation.errors;
    throw err;
  }

  const intakeAt = new Date().toISOString();
  const payload = buildCreatePermitPayload({
    ...formToCreatePayload(form, mode),
    intakeSource: source,
    intakeAt,
  });

  const permit = await createPermitRecord(payload);
  return {
    permit,
    intakeSource: source,
    intakeAt,
    permitId: permit?.id,
  };
}

/** Human-readable intake confirmation for UI. */
export function formatIntakeSuccess(result) {
  const id = result?.permitId || result?.permit?.id;
  const short = id ? String(id).slice(0, 12) : '';
  return id
    ? `Permit ${short}${String(id).length > 12 ? '…' : ''} submitted at intake. Recorded in GovOps audit trail.`
    : 'Permit submitted at intake. Recorded in GovOps audit trail.';
}
